import React, { useMemo } from 'react';
import styled from 'styled-components';

import CostCard from './CostCard';
import { CostCardField, CostCardsGridProps, CostSection, CostSectionColors, CostSectionLabels } from './types';

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
`;

const Header = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
`;

const Title = styled.h2`
    font-size: 16px;
    font-weight: var(--font-weight);
    color: var(--color-primary);
    margin: 0;
`;

const ItemCount = styled.span`
    font-size: var(--font-size-body-2);
    color: var(--color-primary-70);
`;

const Legend = styled.div`
    display: flex;
    gap: 16px;
    margin-left: auto;
    flex-wrap: wrap;
`;

const LegendItem = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
`;

const LegendDot = styled.span<{ $color: string }>`
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: ${(props) => props.$color};
`;

const LegendLabel = styled.span`
    font-size: var(--font-size-body-3);
    color: var(--color-primary-70);
`;

const CardsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 20px;

    @media (max-width: 1200px) {
        grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
    }
`;

const CostCardsGrid: React.FC<CostCardsGridProps> = ({
    fields,
    title = 'Szczegóły kosztów',
    showLegend = true,
    showItemCount = true,
    sectionColors,
    sectionLabels,
    onValueChange,
    onValueBlur,
    onResetField,
    onResetTotalField,
    onTotalValueChange,
    onTotalValueBlur
}) => {
    const mergedSectionColors = useMemo(() => ({
        ...CostSectionColors,
        ...sectionColors
    }), [sectionColors]);

    const mergedSectionLabels = useMemo(() => ({
        ...CostSectionLabels,
        ...sectionLabels
    }), [sectionLabels]);

    const usedSections = useMemo(() => {
        const sections = new Set<CostSection>();
        fields.forEach((field) => {
            if (field.section) {
                sections.add(field.section);
            }
        });

        return Array.from(sections);
    }, [fields]);

    const getCustomColor = (field: CostCardField): string => {
        if (field.customColor) {
            return field.customColor;
        }

        if (field.section) {
            return mergedSectionColors[field.section];
        }

        return '';
    };

    const showHeader = title || showItemCount || showLegend && usedSections.length > 0;

    return (
        <Container>
            {showHeader &&
                <Header>
                    {title && <Title>{title}</Title>}
                    {showItemCount && <ItemCount>{fields.length} Pozycji</ItemCount>}
                    {showLegend && usedSections.length > 0 &&
                        <Legend>
                            {usedSections.map((section) => <LegendItem key={section}>
                                <LegendDot $color={mergedSectionColors[section]} />
                                <LegendLabel>{mergedSectionLabels[section]}</LegendLabel>
                            </LegendItem>)}
                        </Legend>
                    }
                </Header>
            }
            <CardsGrid>
                {fields.map((field) => <CostCard
                    key={field.fieldName}
                    title={field.title}
                    fieldName={field.fieldName}
                    customColor={getCustomColor(field)}
                    value={field.value}
                    totalValue={field.totalValue}
                    suffix={field.suffix}
                    totalValueSuffix={field.totalValueSuffix}
                    showTotalValue={field.showTotalValue}
                    onValueChange={onValueChange}
                    onValueBlur={onValueBlur}
                    onResetField={onResetField}
                    onResetTotalField={onResetTotalField}
                    onTotalValueChange={onTotalValueChange}
                    onTotalValueBlur={onTotalValueBlur}
                    type={field.type}
                    valueLabel={field.valueLabel}
                    totalLabel={field.totalLabel}
                    readOnly={field.readOnly}
                    totalDisabled={field.totalDisabled}
                    required={field.required}
                    isError={field.isError}
                    errorMessage={field.errorMessage}
                />)}
            </CardsGrid>
        </Container>
    );
};

export default CostCardsGrid;
